import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageCircle, Mail, X, Loader2, Send, Globe, Smartphone, Server } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WA_MODES = [
  { key: 'wame',    label: 'wa.me Link',     icon: Smartphone, hint: 'Opens WhatsApp app — manual confirm' },
  { key: 'browser', label: 'WhatsApp Web',   icon: Globe,      hint: 'Opens web.whatsapp.com — manual confirm' },
  { key: 'api',     label: 'API Provider',   icon: Server,     hint: 'Server-side send via configured provider' },
];

/**
 * NotifyCustomerModal — confirmation dialog shown after a Service Job's status
 * changes. Lets the user pick channels (WhatsApp / Email), the WhatsApp delivery
 * mode (wa.me / Web / API), and previews the rendered template before sending.
 */
export function NotifyCustomerModal({ open, onClose, jobId, newStatus, customer = {} }) {
  const [channels, setChannels] = React.useState({ whatsapp: true, email: true });
  const [previews, setPreviews] = React.useState({ whatsapp: null, email: null });
  const [loading, setLoading] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [waMode, setWaMode] = React.useState('wame');     // chosen mode for this send
  const [savedMode, setSavedMode] = React.useState(null); // saved company default

  // Match templates by the LITERAL status master name (case-insensitive equality).
  // The Service Templates UI stores `trigger` exactly as the status master name
  // (e.g. "Invoiced", "Process Invoice", "Test Drive"), so any lossy lower-case
  // mapping breaks the lookup. Keep this in lock-step with backend
  // `_normalize_trigger` in service_templates_routes.py — both must do nothing
  // beyond `.toLowerCase().trim()`.
  const trigger = React.useMemo(
    () => String(newStatus || '').toLowerCase().trim(),
    [newStatus]
  );

  React.useEffect(() => {
    if (!open || !jobId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Fetch all templates to find any matching this trigger + saved WA mode in parallel
        const [{ data: tpls }, waCfgRes] = await Promise.all([
          axios.get(`${API}/service/templates`),
          axios.get(`${API}/whatsapp/config`).catch(() => ({ data: null })),
        ]);
        const matches = (tpls || []).filter((t) =>
          t.enabled && String(t.trigger || '').toLowerCase().trim() === trigger
        );
        const wa = matches.find((t) => t.channel === 'whatsapp');
        const em = matches.find((t) => t.channel === 'email');

        const [waPreview, emPreview] = await Promise.all([
          wa ? axios.post(`${API}/service/templates/preview`, { template_id: wa.id, job_id: jobId }) : Promise.resolve({ data: null }),
          em ? axios.post(`${API}/service/templates/preview`, { template_id: em.id, job_id: jobId }) : Promise.resolve({ data: null }),
        ]);
        if (cancelled) return;
        setPreviews({
          whatsapp: waPreview.data ? { ...waPreview.data, exists: true } : { exists: false },
          email: emPreview.data ? { ...emPreview.data, exists: true } : { exists: false },
        });
        // Auto-disable a channel if no template exists or customer contact is missing
        setChannels((c) => ({
          whatsapp: !!waPreview.data && !!customer.mobile_no_1,
          email: !!emPreview.data && !!customer.email,
        }));
        const sm = (waCfgRes?.data?.mode || 'wame').toLowerCase();
        const valid = ['wame', 'browser', 'api'].includes(sm) ? sm : 'wame';
        setSavedMode(valid);
        setWaMode(valid);
      } catch {
        toast.error('Failed to load notification templates');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, jobId, trigger, customer.mobile_no_1, customer.email]);

  const handleSend = async () => {
    const picked = Object.entries(channels).filter(([, v]) => v).map(([k]) => k);
    if (!picked.length) {
      toast.error('Pick at least one channel');
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/service/jobs/${jobId}/notify`, {
        trigger,
        channels: picked,
        whatsapp_mode: waMode,
      });

      // Backend returns: { trigger, templates_used, dispatched: [{channel, recipient_role, sent, mode?, url?, detail?}] }
      const dispatched = Array.isArray(data?.dispatched) ? data.dispatched : [];
      const labelFor = (ch) => (ch === 'whatsapp' ? 'WhatsApp' : ch === 'email' ? 'Email' : ch);

      // Open any redirect URLs (wa.me / web.whatsapp.com) in new tabs sequentially
      const redirects = dispatched.filter((d) => d?.url && d.channel === 'whatsapp');
      redirects.forEach((d, i) => {
        // small stagger so popup blocker doesn't kill subsequent windows
        setTimeout(() => window.open(d.url, '_blank', 'noopener,noreferrer'), i * 150);
      });

      // Tally per-channel successes
      const successByChannel = dispatched.reduce((acc, d) => {
        if (d?.sent) acc[d.channel] = (acc[d.channel] || 0) + 1;
        return acc;
      }, {});
      const successParts = Object.entries(successByChannel).map(
        ([ch, n]) => `${n} via ${labelFor(ch)}`
      );

      // Collect failures with human-readable reasons
      const failures = dispatched.filter((d) => !d?.sent);
      const reasonOf = (d) => {
        const det = d?.detail;
        if (!det) return 'failed';
        if (typeof det === 'string') {
          if (det === 'no_contact') return 'missing contact';
          return det;
        }
        return det.error || det.message || det.detail || 'failed';
      };

      const modeLabel = waMode === 'wame' ? 'wa.me' : waMode === 'browser' ? 'WhatsApp Web' : 'API';

      if (redirects.length && failures.length === 0) {
        toast.success(
          `Opened ${redirects.length} ${modeLabel} chat${redirects.length > 1 ? 's' : ''} — review and tap Send`,
          { duration: 6000 }
        );
      } else if (successParts.length && failures.length === 0) {
        toast.success(`Sent ${successParts.join(', ')}`);
      } else if (successParts.length && failures.length) {
        const failSummary = failures
          .map((f) => `${labelFor(f.channel)} → ${f.recipient_role}: ${reasonOf(f)}`)
          .join(' • ');
        toast.warning(`Sent ${successParts.join(', ')}. Failed: ${failSummary}`, { duration: 7000 });
      } else if (failures.length) {
        const failSummary = failures
          .map((f) => `${labelFor(f.channel)} → ${f.recipient_role}: ${reasonOf(f)}`)
          .join(' • ');
        toast.error(`Could not send. ${failSummary}`, { duration: 7000 });
      } else if (!data?.templates_used) {
        toast.info('No matching templates for this status — nothing was sent.');
      } else {
        toast.info('No notifications were dispatched (no recipients resolved).');
      }
      onClose(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => onClose(false)} />
      <div className="relative bg-white rounded-2xl shadow-2xl border border-white/50 w-full max-w-lg p-6" data-testid="notify-customer-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-[16px] font-semibold text-slate-900">Notify the customer?</h3>
            <p className="text-[12.5px] text-slate-500 mt-0.5">
              Status changed to <span className="font-medium text-slate-700">{newStatus}</span>. Pick channels and review the message.
            </p>
          </div>
          <button onClick={() => onClose(false)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><X size={16} /></button>
        </div>

        {loading ? (
          <div className="py-8 text-center"><Loader2 size={20} className="animate-spin text-sky-500 mx-auto" /></div>
        ) : (
          <div className="space-y-3">
            {/* WhatsApp */}
            <ChannelRow
              icon={MessageCircle}
              channelKey="whatsapp"
              label="WhatsApp"
              accent="emerald"
              enabled={channels.whatsapp}
              onToggle={(v) => setChannels((c) => ({ ...c, whatsapp: v }))}
              available={previews.whatsapp?.exists}
              missing={!customer.mobile_no_1 ? 'Customer mobile number missing' : null}
              preview={previews.whatsapp?.body}
            />

            {/* WhatsApp Mode Picker — only relevant when WA is selected */}
            {channels.whatsapp && previews.whatsapp?.exists && customer.mobile_no_1 && (
              <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-3" data-testid="wa-mode-picker">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[12px] font-semibold text-emerald-800">Send via</span>
                  {savedMode && savedMode !== waMode && (
                    <button
                      type="button"
                      onClick={() => setWaMode(savedMode)}
                      className="text-[11px] text-emerald-700 hover:underline"
                    >
                      Reset to default ({savedMode})
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {WA_MODES.map((m) => {
                    const Icon = m.icon;
                    const active = waMode === m.key;
                    return (
                      <button
                        key={m.key}
                        type="button"
                        onClick={() => setWaMode(m.key)}
                        data-testid={`wa-mode-${m.key}`}
                        className={`text-left rounded-lg border p-2.5 transition-all ${
                          active
                            ? 'border-emerald-400 bg-white ring-2 ring-emerald-200/60 shadow-sm'
                            : 'border-slate-200 bg-white/60 hover:border-emerald-300'
                        }`}
                      >
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <Icon size={13} className={active ? 'text-emerald-700' : 'text-slate-500'} />
                          <span className={`text-[11.5px] font-semibold ${active ? 'text-emerald-800' : 'text-slate-700'}`}>
                            {m.label}
                          </span>
                        </div>
                        <p className="text-[10.5px] text-slate-500 leading-tight">{m.hint}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Email */}
            <ChannelRow
              icon={Mail}
              channelKey="email"
              label="Email"
              accent="sky"
              enabled={channels.email}
              onToggle={(v) => setChannels((c) => ({ ...c, email: v }))}
              available={previews.email?.exists}
              missing={!customer.email ? 'Customer email missing' : null}
              preview={previews.email?.body}
              subject={previews.email?.subject}
            />
          </div>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            onClick={() => onClose(false)}
            data-testid="notify-skip-btn"
            className="px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 text-[13px]"
          >
            Skip
          </button>
          <button
            onClick={handleSend}
            disabled={sending || loading || (!channels.whatsapp && !channels.email)}
            data-testid="notify-send-btn"
            className="px-4 py-2 rounded-xl text-white text-[13px] font-medium disabled:opacity-60 inline-flex items-center gap-2"
            style={{ background: 'linear-gradient(135deg, #0EA5E9 0%, #6366F1 60%, #DB2777 100%)' }}
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Send Notification
          </button>
        </div>
      </div>
    </div>
  );
}

function ChannelRow({ icon: Icon, channelKey, label, accent, enabled, onToggle, available, missing, preview, subject }) {
  const accentBg = accent === 'emerald' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-sky-50 text-sky-700 border-sky-200';
  const isUsable = available && !missing;
  return (
    <div className={`rounded-xl border ${enabled && isUsable ? accentBg : 'bg-slate-50 border-slate-200 text-slate-500'} p-3`}>
      <div className="flex items-center gap-2.5">
        <input
          type="checkbox"
          checked={enabled}
          disabled={!isUsable}
          onChange={(e) => onToggle(e.target.checked)}
          data-testid={`notify-channel-${channelKey}`}
          className="w-4 h-4 rounded border-slate-300"
        />
        <Icon size={16} />
        <span className="text-[13px] font-semibold">{label}</span>
        {!available && (
          <span className="ml-auto text-[11px] text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">No template for this status</span>
        )}
        {available && missing && (
          <span className="ml-auto text-[11px] text-rose-700 bg-rose-100 px-2 py-0.5 rounded-full">{missing}</span>
        )}
      </div>
      {available && preview && (
        <div className="mt-2 pl-7">
          {subject && <p className="text-[11.5px] font-medium text-slate-700 mb-1">Subject: {subject}</p>}
          <div
            className="text-[12px] text-slate-700 line-clamp-3 whitespace-pre-wrap leading-snug"
            dangerouslySetInnerHTML={{ __html: preview }}
          />
        </div>
      )}
    </div>
  );
}

export default NotifyCustomerModal;
